import { useCallback } from 'react';
import { useAppDispatch } from 'store/hooks';

import { updateLessonById } from 'store/lesson/actions';

import { getChangedPropertiesByKeys } from 'helpers/getChangedPropertiesByKeys';
import { getChangedStructuralProperties } from 'helpers/lessonLocalization.helpers';

import { useLessonLocale } from 'hooks/useLessonLocale';

export const useLocalizedLessonSaveHandlers = ({
  lessonId,
  lessonType,
  baseLesson,
  convertDataToApi,
  getChangedProperties,
  canAutoSave,
  enhanceSaveData,
  getExtraSaveKeys,
  autosaveExcludeStructuralKeys,
}: {
  lessonId: string;
  lessonType: string;
  baseLesson: any;
  convertDataToApi: (values: any) => any;
  getChangedProperties: (args: {
    currentLesson: any;
    nextLesson: any;
  }) => string[];
  canAutoSave?: (values: any) => boolean;
  enhanceSaveData?: (data: Record<string, any>) => Record<string, any>;
  getExtraSaveKeys?: (data: Record<string, any>) => string[];
  autosaveExcludeStructuralKeys?: readonly string[];
}) => {
  const dispatch = useAppDispatch();

  const localeState = useLessonLocale({
    lessonId,
    lessonType,
    baseLesson,
  });

  const { editorLesson, saveLocalizationChanges, mergedLessonLoading } =
    localeState;

  const applySaveDataEnhancements = useCallback(
    (data: Record<string, any>) =>
      enhanceSaveData ? enhanceSaveData(data) : data,
    [enhanceSaveData],
  );

  const saveStructuralChanges = useCallback(
    async (
      data: Record<string, any>,
      options?: { excludeKeys?: readonly string[] },
    ) => {
      const enhancedData = applySaveDataEnhancements(data);
      const changedPropertiesKeys = getChangedStructuralProperties({
        lessonType,
        changedProperties: getChangedProperties({
          currentLesson: baseLesson,
          nextLesson: enhancedData,
        }),
      }).filter((key) => !options?.excludeKeys?.includes(key));

      if (!changedPropertiesKeys.length) {
        return null;
      }

      return dispatch(
        updateLessonById({
          lessonId,
          lessonType,
          data: getChangedPropertiesByKeys({
            data: enhancedData,
            keys: changedPropertiesKeys,
          }),
          changedPropertiesKeys,
        }),
      ).unwrap();
    },
    [
      applySaveDataEnhancements,
      baseLesson,
      dispatch,
      getChangedProperties,
      lessonId,
      lessonType,
    ],
  );

  const handleAutoSaveSubmit = useCallback(
    async (values: any) => {
      if (lessonId !== values.id) {
        return null;
      }

      if (canAutoSave && !canAutoSave(values)) {
        return null;
      }

      const data = convertDataToApi(values);
      let lastUpdated: string | null = null;

      const localizationSaved = await saveLocalizationChanges(data, {
        reloadMergedLesson: false,
      });

      if (localizationSaved) {
        lastUpdated = new Date().toISOString();
      }

      const updatedLesson = await saveStructuralChanges(data, {
        excludeKeys: autosaveExcludeStructuralKeys,
      });

      if (updatedLesson?.updatedAt) {
        lastUpdated = updatedLesson.updatedAt;
      }

      return lastUpdated;
    },
    [
      autosaveExcludeStructuralKeys,
      canAutoSave,
      convertDataToApi,
      lessonId,
      saveLocalizationChanges,
      saveStructuralChanges,
    ],
  );

  const handleSaveClick = useCallback(
    async (values: any) => {
      const data = applySaveDataEnhancements(convertDataToApi(values));

      await saveLocalizationChanges(data);

      const changedPropertiesKeys = [
        ...new Set([
          ...getChangedStructuralProperties({
            lessonType,
            changedProperties: getChangedProperties({
              currentLesson: baseLesson,
              nextLesson: data,
            }),
          }),
          ...(getExtraSaveKeys?.(data) ?? []),
        ]),
      ];

      if (changedPropertiesKeys.length) {
        const updatedLesson = await dispatch(
          updateLessonById({
            lessonId,
            lessonType,
            data,
            changedPropertiesKeys,
          }),
        ).unwrap();

        return updatedLesson.updatedAt;
      }

      return new Date().toISOString();
    },
    [
      applySaveDataEnhancements,
      baseLesson,
      convertDataToApi,
      dispatch,
      getChangedProperties,
      getExtraSaveKeys,
      lessonId,
      lessonType,
      saveLocalizationChanges,
    ],
  );

  return {
    ...localeState,
    editorLesson,
    mergedLessonLoading,
    handleAutoSaveSubmit,
    handleSaveClick,
  };
};
